import React, { useState, useEffect } from "react"
import { useParams, Navigate } from "react-router-dom"
import getAppConfig from "~/utils/getAppConfig"
import { useToast } from "~/utils/Toast"

import { apiWorkspaceAccounts, apiWorkspaceAccountTypes, apiWorkspaceContacts } from "~/routes"
import useFetchResource from "~/hooks/useFetchResource"

import PageContainer from "~/library/PageContainer"
import LoadingWrapper from "~/library/LoadingWrapper"
import AccountForm from "./AccountForm"
import type Account from "~/types/Account"

type AccountTypeOption = {
    id: string
    name: string
}

const EditAccount = () => {
    const { venueSlug } = getAppConfig()
    const { id } = useParams<{ id: string }>()
    const [isUpdated, setIsUpdated] = React.useState(false)
    const [accountContacts, setAccountContacts] = useState<Array<{ id: string; firstName: string; lastName: string }>>([])
    const [isLoadingContacts, setIsLoadingContacts] = useState(false)
    const toast = useToast()

    const { data: account, isLoading: accountLoading } = useFetchResource<
    { account: Account },
    "account"
    >(apiWorkspaceAccounts.show.path({ venue_slug: venueSlug, id: id! }), "account", {})

    const { data: accountTypes, isLoading: typesLoading } = useFetchResource<
    AccountTypeOption[],
    "account_types"
    >(apiWorkspaceAccountTypes.index.path({ venue_slug: venueSlug }), "account_types", {})

    useEffect(() => {
        if (account?.id) {
            fetchAccountContacts(account.id)
        }
    }, [account?.id])

    const fetchAccountContacts = async (accountId: string) => {
        setIsLoadingContacts(true)
        try {
            const url = apiWorkspaceContacts.index.path({ venue_slug: venueSlug })
            const response = await fetch(`${url}?q[account_id_eq]=${accountId}`)
            const data = await response.json()
            setAccountContacts(data.contacts || [])
        } catch (error) {
            console.error("Error fetching account contacts:", error)
        } finally {
            setIsLoadingContacts(false)
        }
    }

    const handleSubmit = async (formValues: any) => {
        try {
            const url = apiWorkspaceAccounts.update.path({
                venue_slug: venueSlug,
                id: id!
            })

            const response = await fetch(url, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    account: formValues,
                }),
            })

            const result = await response.json()

            if (result.status === 'ok') {
                setIsUpdated(true)
                toast("Account updated successfully", "success")
            } else {
                toast(
                    `Error updating account. ${result.errors?.join(", ") || "Please try again"}`,
                    "danger"
                )
            }
        } catch (error) {
            console.error("Error:", error)
            toast("Error updating account. Please try again", "danger")
        }
    }

    if (isUpdated) {
        return <Navigate to={`/accounts/${id}`} />
    }

    const isLoading = accountLoading || typesLoading || isLoadingContacts

    return (
        <PageContainer>
            <LoadingWrapper isLoading={isLoading}>
                <div className="row h-100">
                    <div className="col-8 offset-2 align-center mt-4">
                        <h3 className="mb-3">Edit Account</h3>
                        {account && accountTypes && !isLoadingContacts && (
                            <AccountForm
                                onSubmit={handleSubmit}
                                initialValues={{
                                    name: account.name,
                                    accountTypeId: account.accountType?.id || "",
                                    status: account.status,
                                    address: account.address || "",
                                    website: account.website || "",
                                    industry: account.industry || "",
                                    historicalSpend: account.historicalSpend || null,
                                    lifetimeValue: account.lifetimeValue || null,
                                }}
                                accountTypes={accountTypes}
                                initialContacts={accountContacts}
                                isEditing={true}
                            />
                        )}
                    </div>
                </div>
            </LoadingWrapper>
        </PageContainer>
    )
}

export default EditAccount